import { useCallback } from 'react'

import { PaymentMethodEnum } from 'src/Constants/enums'
import { getCookieByName, getUtmParameters } from 'src/Constants/functions'
import { getValueOrEmptyString } from 'src/Helpers/layoutHelpers'
import { checkoutConfirmedPayment } from 'src/Helpers/TagManager'
import { getAccessToken } from 'src/Redux/auth-process/credentialsSlice/selectors'
import { getUser, getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import { getRememberPayPalForFutureFlag } from 'src/Redux/paypalTransactions-process/selectors'
import { getAllCampaignsCount, useMetadataQuery } from 'src/Redux/metadataApi'
import { useSendPaymentDataMutation } from 'src/Redux/paymentWebhookDataApi'
import { sendSourceBusterEvent } from 'src/Redux/sourcebuster-process/api-actions'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'
import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'

import { useAppDispatch, useAppSelector } from './redux'

export const useAdsOrderCheckoutConfirmEvents = (
  campaignId: number,
  totalBudget: number,
): ((paymentMethod: PaymentMethodEnum) => void) => {
  const dispatch = useAppDispatch()

  const token = useAppSelector(getAccessToken)
  const user = useAppSelector(getUser)
  const userId = useAppSelector(getUserId)
  const isRememberPayPalForFuture = useAppSelector(
    getRememberPayPalForFutureFlag,
  )

  const { data: campaigns } = useMetadataQuery(null)
  const { data: plan } = useGetSubscriptionQuery()
  const [sendPaymentData] = useSendPaymentDataMutation()

  const amountOfCampaigns = getAllCampaignsCount(campaigns)

  return useCallback(
    (paymentMethod: PaymentMethodEnum): void => {
      const utmObj = getUtmParameters(getCookieByName('soundCampaignUtm'))

      void dispatch(
        sendSourceBusterEvent({
          token,
          campaignId,
          campaignPlatform: CampaignPlatformTypeEnum.META,
          eventName: 'purchase_started',
          gclid: getValueOrEmptyString(utmObj.gclid),
        }),
      )

      const resolvedPaymentMethod =
        paymentMethod === PaymentMethodEnum.PAYPAL
          ? PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT
          : paymentMethod

      checkoutConfirmedPayment({
        campaignId,
        isRememberPayPalForFuture,
        paymentMethod: resolvedPaymentMethod,
        campaignType: CampaignTypeEnum.BASIC,
        platformName: CampaignPlatformTypeEnum.META,
        userId: String(userId),
        campaignNumber: amountOfCampaigns || 1,
        genreList: '',
        resultPrice: totalBudget,
        subscriptionPlan: plan?.package?.name || '',
      })

      void sendPaymentData({
        campaignId,
        paymentMethod: resolvedPaymentMethod,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        phone: user.phone_number,
      })
    },
    [
      amountOfCampaigns,
      campaignId,
      dispatch,
      isRememberPayPalForFuture,
      plan?.package?.name,
      sendPaymentData,
      token,
      totalBudget,
      user.email,
      user.first_name,
      user.last_name,
      user.phone_number,
      userId,
    ],
  )
}
